// Service Form Schema for FormBuilder
export const createServiceSchema = {
  initialValues: {
    name: '',
    description: '',
    service_type: '', // No default - user must select from templates
    price: 0,
    quantity: 1,
    billing_period: 'one_time',

    client_id: null,
    project_id: null,
    start_date: new Date().toISOString().split('T')[0], // Default to today
    end_date: '',
    next_billing_date: '',
    auto_renew: false,
    status: 'active',
    notes: ''
  },
  
  validationRules: {
    name: {
      required: true,
      minLength: 2,
      message: 'Το όνομα υπηρεσίας είναι υποχρεωτικό (τουλάχιστον 2 χαρακτήρες)'
    },
    service_type: {
      required: true,
      message: 'Ο τύπος υπηρεσίας είναι υποχρεωτικός'
    },
    price: {
      required: true,
      min: 0,
      message: 'Η τιμή πρέπει να είναι μεγαλύτερη ή ίση με 0'
    },
    client_id: {
      required: true,
      message: 'Ο πελάτης είναι υποχρεωτικός'
    },
    project_id: {
      required: true,
      message: 'Το έργο είναι υποχρεωτικό'
    },
    quantity: {
      required: true,
      min: 1,
      message: 'Η ποσότητα πρέπει να είναι τουλάχιστον 1'
    },
    end_date: {
      required: function(values) {
        return values.billing_period !== 'one_time';
      },
      message: 'Η ημερομηνία λήξης είναι υποχρεωτική για συνδρομητικές υπηρεσίες'
    },
    next_billing_date: {
      required: function(values) {
        return values.billing_period !== 'one_time';
      },
      message: 'Η επόμενη ημερομηνία χρέωσης είναι υποχρεωτική για συνδρομητικές υπηρεσίες'
    }
  },
  
  sections: [
    {
      title: 'Βασικά Στοιχεία',
      fields: [
        {
          name: 'name',
          label: 'Όνομα Υπηρεσίας',
          type: 'text',
          required: true,
          size: 8
        },
        {
          name: 'service_type',
          label: 'Τύπος Υπηρεσίας',
          type: 'select',
          required: true,
          size: 4,
          options: [] // Options will be populated dynamically from templates
        },
        {
          name: 'description',
          label: 'Περιγραφή',
          type: 'text',
          multiline: true,
          rows: 3,
          size: 12
        }
      ]
    },
    {
      title: 'Οικονομικά Στοιχεία',
      fields: [
        {
          name: 'price',
          label: 'Τιμή (€)',
          type: 'number',
          required: true,
          size: 6,
          step: 0.01,
          min: 0
        },
        {
          name: 'quantity',
          label: 'Ποσότητα',
          type: 'number',
          required: true,
          size: 6,
          min: 1
        }
      ]
    },
    {
      title: 'Τύπος Χρέωσης',
      fields: [
        {
          name: 'billing_period',
          label: 'Περίοδος Χρέωσης',
          type: 'select',
          size: 12,
          options: [
            { value: 'one_time', label: 'Εφάπαξ (π.χ. Κατασκευή Ιστοσελίδας)' },
            { value: 'monthly', label: 'Μηνιαία Συνδρομή' },
            { value: 'quarterly', label: 'Τριμηνιαία Συνδρομή' },
            { value: 'semi_annual', label: 'Εξαμηνιαία Συνδρομή' },
            { value: 'annual', label: 'Ετήσια Συνδρομή' },
            { value: 'biennial', label: 'Διετής Συνδρομή' }
          ],
          onChange: function(values, setValue) {
            // Auto-calculate end_date and next_billing_date when billing_period changes
            if (values.billing_period !== 'one_time' && values.start_date) {
              const startDate = new Date(values.start_date);
              let endDate = new Date(startDate);
              let nextBillingDate = new Date(startDate);
              
              switch (values.billing_period) {
                case 'monthly':
                  endDate.setMonth(endDate.getMonth() + 1);
                  nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
                  break;
                case 'quarterly':
                  endDate.setMonth(endDate.getMonth() + 3);
                  nextBillingDate.setMonth(nextBillingDate.getMonth() + 3);
                  break;
                case 'semi_annual':
                  endDate.setMonth(endDate.getMonth() + 6);
                  nextBillingDate.setMonth(nextBillingDate.getMonth() + 6);
                  break;
                case 'annual':
                  endDate.setFullYear(endDate.getFullYear() + 1);
                  nextBillingDate.setFullYear(nextBillingDate.getFullYear() + 1);
                  break;
                case 'biennial':
                  endDate.setFullYear(endDate.getFullYear() + 2);
                  nextBillingDate.setFullYear(nextBillingDate.getFullYear() + 2);
                  break;
                default:
                  // For one_time or other cases, don't set dates
                  break;
              }
              
              setValue('end_date', endDate.toISOString().split('T')[0]);
              setValue('next_billing_date', nextBillingDate.toISOString().split('T')[0]);
            }
          }
        }
      ]
    },
    {
      title: 'Ημερομηνίες',
      fields: [
        {
          name: 'start_date',
          label: 'Ημερομηνία Έναρξης',
          type: 'date',
          size: 4,
          helperText: 'Για εφάπαξ υπηρεσίες: ημερομηνία παράδοσης',
          onChange: function(values, setValue) {
            // Auto-calculate end_date and next_billing_date when start_date changes
            if (values.billing_period !== 'one_time' && values.start_date) {
              const startDate = new Date(values.start_date);
              let endDate = new Date(startDate);
              let nextBillingDate = new Date(startDate);
              
              switch (values.billing_period) {
                case 'monthly':
                  endDate.setMonth(endDate.getMonth() + 1);
                  nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
                  break;
                case 'quarterly':
                  endDate.setMonth(endDate.getMonth() + 3);
                  nextBillingDate.setMonth(nextBillingDate.getMonth() + 3);
                  break;
                case 'semi_annual':
                  endDate.setMonth(endDate.getMonth() + 6);
                  nextBillingDate.setMonth(nextBillingDate.getMonth() + 6);
                  break;
                case 'annual':
                  endDate.setFullYear(endDate.getFullYear() + 1);
                  nextBillingDate.setFullYear(nextBillingDate.getFullYear() + 1);
                  break;
                case 'biennial':
                  endDate.setFullYear(endDate.getFullYear() + 2);
                  nextBillingDate.setFullYear(nextBillingDate.getFullYear() + 2);
                  break;
                default:
                  // For one_time or other cases, don't set dates
                  break;
              }
              
              setValue('end_date', endDate.toISOString().split('T')[0]);
              setValue('next_billing_date', nextBillingDate.toISOString().split('T')[0]);
            }
          }
        },
        {
          name: 'end_date',
          label: 'Ημερομηνία Λήξης',
          type: 'date',
          size: 4,
          conditional: {
            show: function(values) {
              return values.billing_period !== 'one_time';
            }
          },
          helperText: 'Ημερομηνία λήξης της συνδρομής (υπολογίζεται αυτόματα)'
        },
        {
          name: 'next_billing_date',
          label: 'Επόμενη Χρέωση',
          type: 'date',
          size: 4,
          conditional: {
            show: function(values) {
              return values.billing_period !== 'one_time';
            }
          },
          helperText: 'Ημερομηνία που πρέπει να πληρωθεί η επόμενη χρέωση'
        }
      ]
    },
    {
      title: 'Συνδρομητικές Ρυθμίσεις',
      conditional: {
        show: function(values) {
          return values.billing_period !== 'one_time';
        }
      },
      fields: [
        {
          name: 'auto_renew',
          label: 'Αυτόματη Ανανέωση',
          type: 'checkbox',
          size: 12,
          conditional: {
            show: function(values) {
              return values.billing_period !== 'one_time';
            }
          },
          helperText: 'Η υπηρεσία θα ανανεωθεί αυτόματα μετά τη λήξη'
        }
      ]
    },
    {
      title: 'Συσχέτιση',
      fields: [
        {
          name: 'client_id',
          label: 'Πελάτης',
          type: 'client_select',
          required: true,
          size: 6
        },
        {
          name: 'project_id',
          label: 'Έργο',
          type: 'select',
          required: true,
          size: 6
        }
      ]
    },
    {
      title: 'Επιπλέον Πληροφορίες',
      fields: [
        {
          name: 'status',
          label: 'Κατάσταση',
          type: 'select',
          size: 6,
          options: [
            { value: 'active', label: 'Ενεργή' },
            { value: 'on_hold', label: 'Σε Αναμονή' },
            { value: 'grace_period', label: 'Περίοδος Χάριτος' },
            { value: 'suspended', label: 'Αναστολή' },
            { value: 'completed', label: 'Ολοκληρωμένη' },
            { value: 'expired', label: 'Ληγμένη' },
            { value: 'cancelled', label: 'Ακυρωμένη' },
            { value: 'pending', label: 'Εκκρεμής' }
          ]
        },
        {
          name: 'notes',
          label: 'Σημειώσεις',
          type: 'text',
          multiline: true,
          rows: 3,
          size: 12
        }
      ]
    }
  ]
};

export const updateServiceSchema = {
  ...createServiceSchema,
  // Same schema for updates
};
